import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { isEmpty } from '@chiroup/core/functions/isEmpty';
import { UserClinic, UserClinicStatus } from '@chiroup/core/types/User.type';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import userService from '../../../services/user.service';

type Props = {
  clinics?: Partial<UserClinic>[];
  userID?: string;
  refetch: () => void;
};

const UserClinics = ({ clinics, refetch, userID }: Props) => {
  const [leaveModalState, setLeaveModalState] = useState(OpenClosedStates.Closed);
  const [isLeaving, setIsLeaving] = useState(false);
  const [selectedClinicToLeave, setSelectedClinicToLeave] = useState<Partial<UserClinic>>();
  const [selectedClinicToReinstate, setSelectedClinicToReinstate] = useState<Partial<UserClinic>>();
  const [isReinstating, setIsReinstating] = useState(false);
  const [addClinicModalState, setAddClinicModalState] = useState<OpenClosedStates>(
    OpenClosedStates.Closed
  );
  const { hasPermission } = useAdminPermissions();

  const leaveClinic = async (clinicToLeave: Partial<UserClinic>) => {
    setSelectedClinicToLeave(clinicToLeave);
    setLeaveModalState(OpenClosedStates.Open);
  };

  const leaveClinicConfirm = async () => {
    if (isEmpty(selectedClinicToLeave?.ID) || !userID) {
      return;
    }

    const clinicID = selectedClinicToLeave?.ID as number;
    setIsLeaving(true);

    try {
      await userService.removeFromClinic(userID, clinicID);
      setIsLeaving(false);
      setLeaveModalState(OpenClosedStates.Closed);
      setSelectedClinicToLeave(undefined);
      refetch();
    } catch (err) {
      setIsLeaving(false);
    }
  };

  const reinstateClinicConfirm = async () => {
    if (isEmpty(selectedClinicToReinstate?.ID) || !userID) {
      return;
    }

    setIsReinstating(true);

    try {
      await userService.reinstateEducationMembership(
        userID,
        selectedClinicToReinstate?.ID as number
      );
      setSelectedClinicToReinstate(undefined);
      refetch();
    } catch {
      return;
    } finally {
      setIsReinstating(false);
    }
  };

  return (
    <div className="flex flex-col mt-6">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-300 sm:rounded-lg">
            <div className="bg-white w-full border-b border-gray-300">
              <div className="px-6 py-4 bg-white">
                <div className="ml-4">Clinics</div>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-300">
              <tbody className="bg-white divide-y divide-gray-300">
                {clinics?.map(clinic => {
                  const isRemoved =
                    clinic.membershipStatus?.toLowerCase() ===
                    UserClinicStatus.Removed.toLowerCase();

                  return (
                    <tr key={clinic.ID}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="shrink-0 h-10 w-10">
                            <img className="h-10 w-10 rounded-full" src={clinic.logo} alt="" />
                          </div>
                          <div className="ml-4">
                            <Link to={`/clinics/${clinic.ID}`}>
                              <div className="text-sm font-medium text-gray-900">{clinic.name}</div>
                            </Link>
                            <div className="text-sm text-gray-500">{clinic.location}</div>
                            {isRemoved && (
                              <div className="mt-1 w-fit rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                                Removed
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {Array.isArray(clinic.role) ? clinic.role.join(', ') : clinic.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isRemoved && hasPermission('users.reinstate_education_membership') ? (
                          <button
                            type="button"
                            className="relative text-primary-600 hover:text-primary-500"
                            onClick={() => setSelectedClinicToReinstate(clinic)}
                          >
                            <span
                              className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden"
                              aria-hidden="true"
                            />
                            Reinstate
                          </button>
                        ) : !isRemoved && hasPermission('users.remove_from_clinics') ? (
                          <div
                            className="text-primary-600 hover:text-primary-500 cursor-pointer"
                            onClick={leaveClinic.bind(null, clinic)}
                          >
                            Remove
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={leaveModalState === OpenClosedStates.Open}
        confirm={leaveClinicConfirm}
        close={() => {
          setSelectedClinicToLeave(undefined);
          setLeaveModalState(OpenClosedStates.Closed);
        }}
        title="Leave clinic"
        description={`Are you sure you want to leave ${
          selectedClinicToLeave?.name || 'this clinic'
        }?`}
        loading={isLeaving}
        confirmText="Leave"
      />
      <ConfirmModal
        isOpen={Boolean(selectedClinicToReinstate)}
        confirm={reinstateClinicConfirm}
        close={() => setSelectedClinicToReinstate(undefined)}
        title="Reinstate clinic membership"
        description={`Reinstate this user in ${
          selectedClinicToReinstate?.name || 'this education clinic'
        }?`}
        loading={isReinstating}
        confirmText="Reinstate"
      />
    </div>
    // </SectionContainer>
  );
};

export default UserClinics;
